import React, { useEffect } from "react";
import { Routes, Route, useNavigate, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import NavBar from "../components/Navbar";
import Home from "../components/Home";
import { useQuery } from "@apollo/client";
import { Me } from "../graphql/querys/me";
import Context from "../context";
import Perfil from "../components/Perfil";
import { Box } from "@mui/material";
import ChangePassword from "./ChangePassword";
import MachinesScreen from "../screens/machines-screen";
import ClientsScreen from "../screens/clients-screen";
import IconBreadcrumbs from "../components/BreadCrumb";
import Create from "../components/Create";
import MachineScreen from "../screens/Machine";
import ClientScreen from "../screens/Client";
import TechniciansScreen from "../screens/technicians-screen";
import BackofficeUsersScreen from "../screens/backofficeUsers-screen";
import PerfilMobile from "../components/PerfilMobile";
import MachinesScreenMobile from "../screens/machines-screen-mobile";
import MachineScreenMobile from "../screens/Machine_Mobile";
import ManutScreen from "../screens/manut-screen";
import RepairScreen from "../screens/repair-screen";
import InterventionHistory from "../screens/history-screen";
import ItemManager from "../screens/items";
import Dashboard from "../screens/dashboard";
import LogIn from "./Login";
import PublicMachinesForDocuments from "../screens/public-machinhes-documents";
import ProtectedRoute from "./protectedRoute";
import RentingsByUserScreen from "../screens/rentings-by-user-screen";
import OAuthCallback from "../components/OAuthCallback";
import authService, {
	clearStoredAuthToken,
	getStoredAuthToken,
} from "../services/auth-service";

export default function Shell({ onSessionResolved }) {
	const { t } = useTranslation();
	const token = getStoredAuthToken();
	const pathname = window.location.pathname;
	const navigate = useNavigate();
	const isPublicPath = pathname === "/public" || pathname.startsWith("/public/");

	// Initialize auth header when component mounts
	useEffect(() => {
		authService.updateAuthHeader();
	}, []);

	// Handle redirect if authenticated and on login page
	useEffect(() => {
		if (token && pathname === "/login") {
			navigate("/");
		}
	}, [token, pathname, navigate]);

	// Always define the query but skip it when needed
	const { loading, error, data } = useQuery(Me, {
		skip: !token || pathname === "/login" || pathname === "/oauth/callback" || isPublicPath,
		onError: (error) => {
			console.error("Me query error:", error);
		}
	});

	const isUnauthenticated = error?.graphQLErrors?.some(
		(e) => e.extensions?.code === "UNAUTHENTICATED"
	);

	useEffect(() => {
		if (typeof onSessionResolved !== "function") {
			return;
		}

		if (!token || pathname === "/login" || pathname === "/oauth/callback" || isPublicPath) {
			onSessionResolved(null);
			return;
		}

		onSessionResolved(data?.me || null);
	}, [data, isPublicPath, onSessionResolved, pathname, token]);

	useEffect(() => {
		if (!isUnauthenticated) {
			return;
		}

		clearStoredAuthToken();
		if (typeof onSessionResolved === "function") {
			onSessionResolved(null);
		}
	}, [isUnauthenticated, onSessionResolved]);

	if (!isPublicPath && pathname !== "/login" && pathname !== "/oauth/callback") {
		if (!token) {
			return <Navigate to="/login" />;
		}

		if (loading) return <div>{t("COMMON.LOADING")}</div>;
		if (error) {
			if (isUnauthenticated) {
				return <Navigate to="/login" />;
			}
			return <div>{t("COMMON.SESSION_LOAD_ERROR")}</div>;
		}
	}

	return (
		<>
			<Routes>
				<Route path="/login" element={<LogIn />} />
				<Route path="/oauth/callback" element={<OAuthCallback />} />
				<Route path="/public/:id" element={<PublicMachinesForDocuments />} />
				<Route
					path="*"
					element={
						<ProtectedRoute token={token}>
							<Context.Provider value={data?.me}>
								<Box sx={{ display: "flex" }}>
									<NavBar />
									<Routes>
										<Route path="/" element={<Home />} />
										<Route path="/perfil" element={<Perfil />} />
										<Route path="/perfil-mobile" element={<PerfilMobile />} />
										<Route path="/perfil-mobile/:id" element={<MachineScreenMobile />} />
										<Route path="/change-password" element={<ChangePassword />} />
										<Route path="/machines" element={<MachinesScreen />} />
										<Route
											path="/machines-mobile"
											element={<MachinesScreenMobile />}
										/>
										<Route path="/machines/:id" element={<MachineScreen />} />
										<Route
											path="/machines-mobile/:id"
											element={<MachineScreenMobile />}
										/>
										<Route path="/clients" element={<ClientsScreen />} />
										<Route path="/clients/:id" element={<ClientScreen />} />
										<Route path="/technicians" element={<TechniciansScreen />} />
										<Route
											path="/backofficeWorkers"
											element={<BackofficeUsersScreen />}
										/>
										<Route path="/create" element={<Create />} />
										<Route path="/manut" element={<ManutScreen />} />
										<Route path="/manut/:id" element={<ManutScreen />} />
										<Route path="/repair" element={<RepairScreen />} />
										<Route path="/repair/:id" element={<RepairScreen />} />
										<Route path="/history/:id" element={<InterventionHistory />} />
										<Route path="/items" element={<ItemManager />} />
										<Route path="/dashboard" element={<Dashboard />} />
										<Route
											path="/rentings-by-user"
											element={<RentingsByUserScreen />}
										/>
									</Routes>
								</Box>
							</Context.Provider>
						</ProtectedRoute>
					}
				/>
			</Routes>
		</>
	);
}
